import {
  ApiHiringFlowField,
  HiringFlowSectionType,
  HiringFlowEditorSection,
} from "@/types/hiring-flow.types";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { Button, FontAwesomeIcon, Stack, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { HiringFlowSection } from "@/pages/settings/hiring-flows/edit/components/HiringFlowSection";
import { HiringFlowSectionModal } from "@/pages/settings/hiring-flows/edit/widgets/HiringFlowSectionModal";

export function HiringFlowFields({
  sections,
  onChange,
  disabled,
}: {
  sections: HiringFlowEditorSection[];
  onChange: (sections: HiringFlowEditorSection[]) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const sectionTitle = (section: HiringFlowEditorSection) =>
    section.title || t(`hiring_flow:section_${section.section_type}`);
  const updateField = (sectionKey: string, slug: string, changes: Partial<ApiHiringFlowField>) => {
    onChange(
      sections.map((section) =>
        section.key !== sectionKey
          ? section
          : {
              ...section,
              fields: section.fields.map((field) =>
                field.field.slug !== slug ? field : { ...field, ...changes }
              ),
            }
      )
    );
  };
  const onDragEnd = ({ source, destination, type }: DropResult) => {
    if (!destination || disabled) return;
    const next = sections.map((section) => ({ ...section, fields: [...section.fields] }));
    if (type === "section") {
      const [section] = next.splice(source.index, 1);
      next.splice(destination.index, 0, section);
    } else {
      const from = next.find((section) => section.key === source.droppableId);
      const to = next.find((section) => section.key === destination.droppableId);
      if (!from || !to) return;
      const [field] = from.fields.splice(source.index, 1);
      to.fields.splice(destination.index, 0, field);
    }
    onChange(next);
  };
  return (
    <Stack>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="section">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {sections.map((section, sectionIndex) => (
                <HiringFlowSection
                  key={section.key}
                  section={section}
                  index={sectionIndex}
                  title={sectionTitle(section)}
                  disabled={disabled}
                  onEdit={() =>
                    openModal(HiringFlowSectionModal, {
                      initialTitle: section.title,
                      sectionType: section.section_type,
                      onSave: (title: string | null) =>
                        onChange(
                          sections.map((item) =>
                            item.key === section.key ? { ...item, title } : item
                          )
                        ),
                    })
                  }
                  onDelete={() => onChange(sections.filter((item) => item.key !== section.key))}
                  onFieldChange={(slug, changes) => updateField(section.key, slug, changes)}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Button
        disabled={disabled}
        className="w-full mt-4"
        variant="secondary"
        size={"lg"}
        leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
        onClick={() =>
          openModal(HiringFlowSectionModal, {
            onSave: (title: string | null) => {
              if (title === null) return;
              onChange([
                ...sections,
                {
                  key: crypto.randomUUID(),
                  section_type: HiringFlowSectionType.custom,
                  title,
                  fields: [],
                },
              ]);
            },
          })
        }
      >
        {t("hiring_flow:add_section")}
      </Button>
    </Stack>
  );
}
